import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../AuthContext";
import OAuthRegisterModal from "./OAuthRegisterModal";

const YANDEX_ERROR_MESSAGES = {
  no_token: "Для входа через Яндекс не передан токен. Попробуйте еще раз.",
  yandex_auth_failed: "Не удалось получить данные от Яндекса. Проверьте вход в аккаунт и попробуйте снова.",
  no_email_from_yandex:
    "У вас не добавлена почта в Яндексе. Добавьте email в аккаунт Яндекса и попробуйте снова.",
};

function getYandexErrorMessage(payload, fallback) {
  if (!payload) {
    return fallback;
  }

  if (typeof payload.error === "string" && YANDEX_ERROR_MESSAGES[payload.error]) {
    return YANDEX_ERROR_MESSAGES[payload.error];
  }

  if (typeof payload.message === "string" && payload.message.trim()) {
    return payload.message;
  }

  return fallback;
}

export async function loginWithYandex(accessToken) {
  const res = await fetch("/api/auth/yandex/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ access_token: accessToken }),
  });

  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new Error(getYandexErrorMessage(payload, "Ошибка входа через Яндекс"));
  }

  return res.json();
}

export async function registerPendingOAuth({ privacy_consent, marketing_consent }) {
  const res = await fetch("/api/auth/auth0/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      privacy_consent,
      marketing_consent,
    }),
  });

  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new Error(getYandexErrorMessage(payload, "Ошибка регистрации через Яндекс"));
  }

  return res.json();
}

export default function YandexLoginButton({ disabled }) {
  const { fetchCurrentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [localError, setLocalError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingModalVisible, setPendingModalVisible] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const popupRef = useRef(null);
  const clientId = "331d2e03ec4f4f5caa9370f9e4a7ad77";
  const origin = typeof window === "undefined" ? "https://pulseads.ru" : window.location.origin;
  const tokenPage = `${origin}/yandex-token.html`;

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    let cancelled = false;

    const handleMessage = async (event) => {
      if (event.origin !== origin) {
        return;
      }

      const accessToken =
        event.data?.access_token ||
        event.data?.token ||
        event.data?.payload?.access_token;

      if (!accessToken) {
        return;
      }

      try {
        setLoading(true);
        setLocalError(null);

        const res = await loginWithYandex(accessToken);

        if (res?.registered === false) {
          setPendingUser({ email: res.email, name: res.name });
          setPendingModalVisible(true);
          return;
        }

        if (res?.token) {
          localStorage.setItem("user_token", res.token);
          localStorage.setItem("user_role", res?.user?.role || "user");
        }

        await fetchCurrentUser();
        navigate("/dashboard");
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error("Ошибка входа через Яндекс:", err);

        const errorMessage = err.message || "Ошибка входа через Яндекс";
        setLocalError(errorMessage);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      cancelled = true;
      window.removeEventListener("message", handleMessage);
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, [fetchCurrentUser, navigate, origin]);

  const handleClick = () => {
    if (disabled) {
      return;
    }

    setLocalError(null);

    const authUrl = new URL("https://oauth.yandex.ru/authorize");
    authUrl.searchParams.set("response_type", "token");
    authUrl.searchParams.set("client_id", clientId);
    authUrl.searchParams.set("redirect_uri", tokenPage);

    const width = 520;
    const height = 720;
    const left = Math.max(window.screenX + (window.outerWidth - width) / 2, 0);
    const top = Math.max(window.screenY + (window.outerHeight - height) / 2, 0);

    popupRef.current = window.open(
      authUrl.toString(),
      "yandex-oauth",
      `popup=yes,width=${width},height=${height},left=${Math.round(left)},top=${Math.round(top)}`
    );

    if (!popupRef.current) {
      setLocalError("Браузер заблокировал окно авторизации. Разрешите popup и попробуйте снова.");
    }
  };

  const authButtonStyles = {
    width: 44,
    height: 44,
    border: "none",
    borderRadius: 8,
    background: disabled ? "#999" : "#fc3f1d",
    color: "#fff",
    fontSize: 24,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: disabled ? 0.6 : 1,
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleClick}
        style={authButtonStyles}
        aria-label="Войти через Яндекс"
        title={disabled ? "Отметьте согласие с Политикой на вкладке регистрации" : "Войти через Яндекс"}
        disabled={disabled}
      >
        Я
      </button>
      {loading && (
        <div style={{ fontSize: 12, marginTop: 4 }}>
          Авторизация через Яндекс…
        </div>
      )}
      {localError && (
        <div style={{ fontSize: 12, color: "red", marginTop: 4 }}>
          {localError}
        </div>
      )}

      <OAuthRegisterModal
        isOpen={pendingModalVisible}
        providerName="Яндекс"
        email={pendingUser?.email}
        name={pendingUser?.name}
        onClose={() => setPendingModalVisible(false)}
        onSubmit={async ({ privacy_consent, marketing_consent }) => {
          const res = await registerPendingOAuth({
            privacy_consent,
            marketing_consent,
          });

          if (res?.token) {
            localStorage.setItem("user_token", res.token);
            localStorage.setItem("user_role", res?.user?.role || "user");
          }

          setPendingModalVisible(false);
          await fetchCurrentUser();
          navigate("/dashboard");
        }}
      />
    </div>
  );
}
